/**
 * API Service for Predictions & Planning
 * Handles predictions execution and planning data retrieval
 * NO MOCK DATA - Real API calls only
 */

import apiClient from './index';

/**
 * Validate that a tag exists in ROBIN API
 * @param {string} tag - The tag name to validate
 * @returns {Promise<Object>} Validation result with exists boolean
 */
export async function validateTag(tag) {
  if (!tag || tag.trim() === '') {
    return { exists: false, error: 'Le tag est vide' };
  }

  try {
    const response = await apiClient.get('/robin/validate-tag', {
      params: { tag: tag.trim() }
    });

    return {
      exists: response.data.exists === true,
      tagInfo: response.data.tagInfo || null,
      error: response.data.exists ? null : `Le tag "${tag}" n'existe pas dans ROBIN`
    };
  } catch (error) {
    console.error('Error validating tag:', error);
    const errorMessage = error.response?.data?.error 
      || error.response?.data?.message 
      || error.message 
      || 'Erreur lors de la validation du tag';
    
    return { exists: false, error: errorMessage };
  }
}

/**
 * Run prediction calculation - REAL DATA ONLY
 * @param {string} assetId - Asset/Heating Plant ID
 * @param {Object} params - Prediction parameters
 * @returns {Promise<Object>} Prediction results with planning data
 */
export async function runPrediction(assetId, params) {
  console.log('Running prediction for asset:', assetId);
  console.log('Prediction parameters:', params);

  // Validate tag first
  if (params.tagPrevision) {
    const tagValidation = await validateTag(params.tagPrevision);
    if (!tagValidation.exists) {
      throw new Error(tagValidation.error || `Tag "${params.tagPrevision}" invalide`);
    }
  }

  // Call backend API for real calculation
  const response = await apiClient.post('/predictions/calculate', {
    assetId,
    initialStocks: params.initialStocks,
    startDate: params.startDate,
    endDate: params.endDate,
    constraints: params.constraints || [],
    tagPrevision: params.tagPrevision || null,
  });

  if (response.data && response.data.success) {
    return {
      success: true,
      planningData: response.data.planningData,
      metadata: {
        assetId,
        generatedAt: new Date().toISOString(),
        parameters: params,
      },
    };
  }

  throw new Error(response.data?.error || 'Réponse invalide du serveur');
}

/**
 * Save simulation result to the database for a given asset (upsert).
 * @param {string} assetId - Asset/Heating Plant ID
 * @param {Array} planningData - The planning data array returned by runPrediction
 */
export async function saveSimulationResult(assetId, planningData) {
  const response = await apiClient.post(`/assets/${assetId}/simulation-result`, { planningData });
  return response.data;
}

/**
 * Retrieve the last stored simulation result for a given asset.
 * @param {string} assetId - Asset/Heating Plant ID
 * @returns {Promise<{planningData: Array, createdAt: string|null}>}
 */
export async function getLastSimulationResult(assetId) {
  const response = await apiClient.get(`/assets/${assetId}/simulation-result`);
  return {
    planningData: response.data.planning_data || [],
    createdAt: response.data.created_at || null,
  };
}

/**
 * Get existing planning data for an asset
 */
export async function getPlanning(assetId, planningId = null) {
  const response = await apiClient.get(
    planningId ? `/plannings/${planningId}` : `/heatingplants/${assetId}/plannings`
  );
  return response.data;
}

/**
 * Add a constraint
 */
export async function addConstraint(plantId, siloId, constraint) {
  const response = await apiClient.post(`/silos/${siloId}/rules`, {
    type: constraint.type,
    date: constraint.date,
    endDate: constraint.endDate,
    quantity: constraint.quantity,
    value: constraint.value,
    description: constraint.description,
  });
  return response.data;
}

/**
 * Delete a constraint
 */
export async function deleteConstraint(plantId, siloId, constraintId) {
  await apiClient.delete(`/heatingplants/${plantId}/silos/${siloId}/rules/${constraintId}`);
}

/**
 * Get snapshots (stock history)
 */
export async function getSnapshots(plantId, siloId, startDate, endDate) {
  const response = await apiClient.get(`/heatingplants/${plantId}/snapshots`, {
    params: { siloId, startDate, endDate },
  });
  return response.data;
}
